import React, { useEffect, useRef, useState } from "react";
import styles from "../Advancetrees.module.css";

type Operation = "insert" | "searchWord" | "searchPrefix" | "delete";

interface TrieNode {
  id: number;
  char: string | null;
  children: Record<string, TrieNode>;
  isEnd: boolean;
  x?: number;
  y?: number;
}

interface TrieEngineProps {
  trieWords: string[];
  inputWord: string;
  operation: Operation | null;
  resetTrigger: boolean;
  generateTrigger: number;
  setActiveLine: (line: number | null) => void;
  setHighlightedNodes: React.Dispatch<React.SetStateAction<string[]>>;
}

const TrieEngine: React.FC<TrieEngineProps> = ({
  trieWords,
  inputWord,
  operation,
  resetTrigger,
  generateTrigger,
  setActiveLine,
  setHighlightedNodes,
}) => {
  const [root, setRoot] = useState<TrieNode | null>(null);
  const [activeNodeIds, setActiveNodeIds] = useState<number[]>([]);
  const [foundNodeIds, setFoundNodeIds] = useState<number[]>([]);
  const [missingNodeIds, setMissingNodeIds] = useState<number[]>([]);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const prevWordsRef = useRef<string[]>([]);
  
  const buildTrie = (words: string[]): TrieNode | null => {    
    if (words.length === 0) return null;
    let idCounter = 0;
    const rootNode: TrieNode = {
      id: idCounter++,
      char: null,
      children: {},
      isEnd: false,
    };

    for (const raw of words) {
      const word = raw.toLowerCase();
      if (!word) continue;
      let node = rootNode;
      for (const ch of word) {
        if (!node.children[ch]) {
          node.children[ch] = {
            id: idCounter++,
            char: ch,
            children: {},
            isEnd: false,
          };
        }
        node = node.children[ch];
      }
      node.isEnd = true;
    }

    const xGap = 80;
    const levelHeight = 90;
    let leafIndex = 0;

    const dfsPos = (node: TrieNode, depth: number) => {
      const keys = Object.keys(node.children).sort();
      if (keys.length === 0) {
        const x = leafIndex * xGap;
        leafIndex++;
        node.x = x;
      } else {
        keys.forEach((k) => dfsPos(node.children[k], depth + 1));
        const firstChild = node.children[keys[0]];
        const lastChild = node.children[keys[keys.length - 1]];
        node.x = ((firstChild.x ?? 0) + (lastChild.x ?? 0)) / 2;
      }
      node.y = 80 + depth * levelHeight;
    };

    dfsPos(rootNode, 0);

    const totalWidth = (Math.max(leafIndex - 1, 0)) * xGap;
    const offset = -totalWidth / 2;
    const shift = (node: TrieNode) => {
      node.x = (node.x ?? 0) + offset;
      const keys = Object.keys(node.children);
      keys.forEach((k) => shift(node.children[k]));
    };
    shift(rootNode);
    return rootNode;
  };

  const getPath = (rootNode: TrieNode, word: string) => {
    const pathNodes: TrieNode[] = [];
    let node: TrieNode | null = rootNode;
    let missingIndex = -1;

    for (let i = 0; i < word.length; i++) {
      if (!node) {
        missingIndex = i;
        break;
      }
      const ch = word[i];
      const next : TrieNode | undefined = node.children[ch];
      if (!next) {
        missingIndex = i;
        break;
      }
      pathNodes.push(next);
      node = next;
    }

    return { pathNodes, missingIndex };
  };

  const collectEndNodes = (node: TrieNode, endIds: number[]) => {
    if (node.isEnd && node.char !== null) {
      endIds.push(node.id);
    }
    const keys = Object.keys(node.children);
    keys.forEach((k) => collectEndNodes(node.children[k], endIds));
  };

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setActiveNodeIds([]);
    setFoundNodeIds([]);
    setMissingNodeIds([]);
    setActiveLine(null);
    setHighlightedNodes([]);
  }, [resetTrigger]);

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setActiveNodeIds([]);
    setFoundNodeIds([]);
    setMissingNodeIds([]);
    setActiveLine(null);
    setHighlightedNodes([]);

    const builtRoot = buildTrie(trieWords);
    setRoot(builtRoot);

    if (!builtRoot || !operation) {
      prevWordsRef.current = trieWords;
      return;
    }

    const prevWords = prevWordsRef.current;
    let word = inputWord.trim().toLowerCase();

    if (operation === "insert") {
      const added = trieWords.filter((w) => !prevWords.includes(w));
      if (added.length > 0) word = added[0].toLowerCase();
    } else if (operation === "delete") {
      const removed = prevWords.filter((w) => !trieWords.includes(w));
      if (removed.length > 0) word = removed[0].toLowerCase();
    }

    if (!word) {
      prevWordsRef.current = trieWords;
      return;
    }

    const { pathNodes, missingIndex } = getPath(builtRoot, word);
    const STEP_MS = 700;

    const schedule = (fn: () => void, delay: number) => {
      const t = setTimeout(fn, delay);
      timersRef.current.push(t);
    };

    if (operation === "insert") {
      pathNodes.forEach((node, idx) => {
        schedule(() => {
          setActiveNodeIds((prev) => [...prev, node.id]);
          setHighlightedNodes((prev) => [...prev, word.slice(0, idx + 1)]);      
          const seq = [1, 2, 3, 4, 5];
          if (idx < pathNodes.length - 1) {
            setActiveLine(seq[idx % seq.length]);
          } else {
            setActiveLine(6); 
          }
        }, idx * STEP_MS);
      });
    } else if (operation === "searchWord") {
      const lastIdx = pathNodes.length - 1;
      pathNodes.forEach((node, idx) => {
        schedule(() => {
          setActiveNodeIds((prev) => [...prev, node.id]);
          setHighlightedNodes((prev) => [...prev, word.slice(0, idx + 1)]);
          const seq = [1, 2, 5];
          if (idx < lastIdx) {
            setActiveLine(seq[idx % seq.length]);
          } else {
            if (missingIndex !== -1 || !node.isEnd || pathNodes.length < word.length) {
              setMissingNodeIds([node.id]);
              setActiveLine(4); 
            } else {
              setFoundNodeIds([node.id]);
              setActiveLine(6); 
            }
          }
        }, idx * STEP_MS);
      });

      if (pathNodes.length === 0) {
        schedule(() => {
          setActiveLine(4); 
        }, 0);
      }
    } else if (operation === "searchPrefix") {
      const lastIdx = pathNodes.length - 1;
      pathNodes.forEach((node, idx) => {
        schedule(() => {
          setActiveNodeIds((prev) => [...prev, node.id]);
          setHighlightedNodes((prev) => [...prev, word.slice(0, idx + 1)]);
          const seq = [1, 2, 5];
          if (idx < lastIdx) {
            setActiveLine(seq[idx % seq.length]);
          } else {
            setActiveLine(6); 
          }
        }, idx * STEP_MS);
      });

      const afterPrefixDelay = Math.max(pathNodes.length, 1) * STEP_MS;
      schedule(() => {
        if (missingIndex !== -1 || pathNodes.length < word.length) {
          if (pathNodes.length > 0) {
            const lastNode = pathNodes[pathNodes.length - 1];
            setMissingNodeIds([lastNode.id]);
            setActiveLine(4); 
          } else {
            setActiveLine(4);
          }
        } else {
          const lastNode = pathNodes[pathNodes.length - 1];
          const endIds: number[] = [];
          collectEndNodes(lastNode, endIds);
          setFoundNodeIds(endIds);
          setActiveLine(6);
        }
      }, afterPrefixDelay);
    } else if (operation === "delete") {
      const lastIdx = pathNodes.length - 1;
      if (pathNodes.length === 0) {
        schedule(() => {
          setActiveLine(1); 
        }, 0);
      } else {
        pathNodes.forEach((node, idx) => {
          schedule(() => {
            setActiveNodeIds((prev) => [...prev, node.id]);
            setHighlightedNodes((prev) => [...prev, word.slice(0, idx + 1)]);
            const seq = [1, 2, 3, 7, 8];
            if (idx < lastIdx) {
              setActiveLine(seq[idx % seq.length]);
            } else {
              if (!node.isEnd) {
                setMissingNodeIds([node.id]);
                setActiveLine(10); 
              } else {
                setFoundNodeIds([node.id]);
                setActiveLine(4);
              }
            }
          }, idx * STEP_MS);
        });
      }
    }

    const totalSteps = Math.max(pathNodes.length, 1);

    const cleanupTimer = setTimeout(() => {
      setActiveLine(null);
    }, totalSteps * STEP_MS + 800);
    timersRef.current.push(cleanupTimer);
    prevWordsRef.current = trieWords;
  }, [trieWords, operation, inputWord, generateTrigger]);

  const nodes: TrieNode[] = [];
  const edges: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];
  const traverse = (node: TrieNode | null) => {
    if (!node) return;
    nodes.push(node);
    const keys = Object.keys(node.children).sort();
    keys.forEach((k) => {
      const child = node.children[k];
      edges.push({
        x1: node.x ?? 0,
        y1: node.y ?? 0,
        x2: child.x ?? 0,
        y2: child.y ?? 0,
        key: `${node.id}-${child.id}`,
      });
      traverse(child);
    });
  };

  traverse(root);

  const getDepth = (node: TrieNode | null): number => {
    if (!node) return 0;
    const keys = Object.keys(node.children);
    if (keys.length === 0) return 1;
    return (
      1 +
      Math.max(...keys.map((k) => getDepth(node.children[k])))
    );
  };

  const depth = getDepth(root);
  const svgHeight = Math.max(350, depth * 110);
  const viewBoxHeight = svgHeight;

  return (
    <div className={styles.heapViz}>
      <svg
        width="100%"
        height={svgHeight}
        viewBox={`-500 0 1000 ${viewBoxHeight}`}
      >
        {edges.map((edge) => (
          <line
            key={edge.key}
            x1={edge.x1}
            y1={edge.y1}
            x2={edge.x2}
            y2={edge.y2}
            className={styles.heapLine}
          />
        ))}

        {nodes.map((node) => {
          if (node.char === null) return null; 
          let nodeClass = styles.trieNode;
          if (missingNodeIds.includes(node.id)) {
            nodeClass += ` ${styles.highlightMissing}`;
          } else if (foundNodeIds.includes(node.id)) {
            nodeClass += ` ${styles.highlightFound}`;
          } else if (activeNodeIds.includes(node.id)) {
            nodeClass += ` ${styles.highlightActive}`;
          }

          const x = node.x ?? 0;
          const y = node.y ?? 0;

          return (
            <g key={node.id}>
              <circle cx={x} cy={y} r={20} className={nodeClass} />
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                className={styles.nodeLabel}
              >
                {node.char}
              </text>
              {node.isEnd && (
                <text
                  x={x + 18}
                  y={y - 8}
                  className={styles.endMark}
                >
                  ✓
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default TrieEngine;